import React from "react";
import PropTypes from "prop-types";
import ReactSelect, { components } from "react-select";
import classNames from "classnames";
import { IconClose16 } from "../../Icons/Icons";
import "./select.scss";

const renderOptions = (options, id, subLabel) => {
  return options.map(option => {
    if (option.options) {
      const groupName = `${id}_group_${option.id ? option.id : option.label}`;
      return (
        <optgroup key={groupName} label={option.label}>
          {renderOptions(option.options)}
        </optgroup>
      );
    }
    return (
      <option key={option.value} value={option.value}>
        {option.label}
        {subLabel && `-${subLabel}`}
      </option>
    );
  });
};

export const getNativeSafeValue = (value, multiple) => {
  if (!value && multiple) {
    return [];
  }
  if (multiple) {
    return value.map(v => v.value);
  }
  return value ? value.value : "";
};

const MultiValueRemove = props => {
  return (
    <components.MultiValueRemove {...props}>
      <IconClose16 />
    </components.MultiValueRemove>
  );
};

const SingleValue = ({ data, ...singleValueProps }, isCountryCode) => {
  const { value, label, icon, subLabel } = data;
  if (isCountryCode) {
    return <components.SingleValue {...singleValueProps}>{value}</components.SingleValue>;
  }
  return (
    <components.SingleValue {...singleValueProps}>
      {icon && <img src={icon} className="react-select__option-icon" alt="" />}
      {label}
      {subLabel && <div>{subLabel}</div>}
    </components.SingleValue>
  );
};

const Option = ({ data, ...optionProps }) => {
  const { label, icon, subLabel } = data;
  return (
    <components.Option {...optionProps}>
      {icon && <img src={icon} className="react-select__option-icon" alt="" />}
      {label}
      {subLabel && <div>{subLabel}</div>}
    </components.Option>
  );
};

const Select = ({
  id,
  options,
  value,
  defaultValue,
  onChange,
  placeholder,
  disabled,
  className,
  hasJumpList,
  isCountryCode,
  multiple,
  required,
  isSearchable,
  ...props
}) => {
  const handleNativeChange = e => {
    if (multiple) {
      const selectedOptions = [].slice.call(e.target.selectedOptions);

      const results = selectedOptions
        ? selectedOptions.map(opt => ({ label: opt.label, value: opt.value }))
        : [];
      return onChange(results);
    }

    // eslint-disable-next-line no-unused-vars
    const [selectedOption, ...rest] = e.target.selectedOptions;
    const result = selectedOption
      ? { label: selectedOption.label, value: selectedOption.value }
      : null;

    return onChange(result);
  };

  const nativeSelectClasses = classNames("native-select", {
    "native-select--has-jumplist": hasJumpList,
  });

  const reactSelectClasses = classNames("react-select-container", {
    "react-select-container--has-jumplist": hasJumpList,
  });

  const safeValue = getNativeSafeValue(value, multiple);

  return (
    <div className={className} {...props}>
      <select
        id={id}
        onChange={handleNativeChange}
        value={safeValue}
        className={nativeSelectClasses}
        disabled={disabled}
        multiple={multiple}
        required={required}
      >
        <option key={`${id}_null`} value="">
          {placeholder}
        </option>
        {renderOptions(options, id)}
      </select>
      <label htmlFor="react-select-label">
        <ReactSelect
          aria-label={id}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          className={reactSelectClasses}
          classNamePrefix="react-select"
          isSearchable={isSearchable}
          isDisabled={disabled}
          options={options}
          placeholder={placeholder}
          required={required}
          components={{
            SingleValue: singleValue => SingleValue(singleValue, isCountryCode),
            MultiValueRemove,
            Option,
          }}
          isMulti={multiple}
        />
      </label>
    </div>
  );
};

const optionPropTypes = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
});

Select.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([optionPropTypes, PropTypes.arrayOf(optionPropTypes)]),
  defaultValue: optionPropTypes,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  hasJumpList: PropTypes.bool,
  isCountryCode: PropTypes.bool,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  isSearchable: PropTypes.bool,
};

export default Select;
